/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from 'react';
import { HiPlus } from 'react-icons/hi';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

import api from '~/services/api';
import Toast from '~/utils/toast';

import { Cards, Container, Filter } from './styles';
import FiltersLists from '~/components/FiltersLists';
import mask from '~/utils/mask';

import { KrClose, KrEdit } from '~/components/KoroIcons';

import InfiniteScroll from '~/components/InfiniteScroll';
import Loading from '~/components/Loading';

interface ISponsorData {
  id: number;
  name: string;
  created_at: string;
  logo: {
    archive_url: string;
  };
  company: {
    document: string;
  };
  sponsorsEvents: {
    id: number;
  }[];
}

interface ISponsorResponse {
  data: ISponsorData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface ISponsor {
  id: number;
  logo: string;
  sponsor: string;
  cnpj: string;
  date: Date;
  totalEvents: number;
}

const Sponsors: React.FC = () => {
  const [sponsors, setSponsors] = useState<ISponsor[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sponsorName, setSponsorName] = useState('');
  const [dateStartSelected, setDateStartSelected] = useState<
    string | undefined
  >(undefined);
  const [dateEndSelected, setDateEndSelected] = useState<string | undefined>(
    undefined
  );

  const handleLoadSponsors = useCallback(
    async (
      pageData,
      sponsor?: string,
      dateStart?: string,
      dateEnd?: string
    ) => {
      try {
        setLoadingMore(true);
        const response = await api.get<ISponsorResponse>('sponsors', {
          params: {
            page: pageData,
            sponsor,
            dateStart,
            dateEnd,
          },
        });

        const data = response.data.data.map<ISponsor>((sponsorData) => ({
          id: sponsorData.id,
          logo: sponsorData.logo.archive_url,
          sponsor: sponsorData.name,
          cnpj: sponsorData.company.document,
          date: parseISO(sponsorData.created_at),
          totalEvents: sponsorData.sponsorsEvents.length,
        }));

        if (pageData === 1) {
          setSponsors(data);
        } else {
          setSponsors((oldState) => [...oldState, ...data]);
        }

        setTotalPages(response.data.pages);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    handleLoadSponsors(1);
  }, [handleLoadSponsors]);

  const handleClickDelete = useCallback((sponsor) => {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ff6900',
      title: `Gostaria de excluir o patrocinador?`,
      showCancelButton: true,
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#B34A00',
      reverseButtons: true,
    }).then(async (e) => {
      if (e.isConfirmed) {
        await api.delete(`sponsors/${sponsor.id}`);
        setSponsors((oldState) =>
          oldState.filter((sponsorData) => sponsorData.id !== sponsor.id)
        );
        Toast.fire({
          icon: 'success',
          iconColor: '#ff6900',
          title: 'Patrocinador excluído com sucesso!',
        });
      }
    });
  }, []);

  const handleLoad = useCallback(async () => {
    if (!loadingMore) {
      setLoadingMore(true);
      if (page < totalPages) {
        await handleLoadSponsors(
          page + 1,
          sponsorName,
          dateStartSelected,
          dateEndSelected
        );
        setPage(page + 1);
      } else {
        setLoadingMore(false);
      }
    }
  }, [
    dateEndSelected,
    dateStartSelected,
    handleLoadSponsors,
    loadingMore,
    page,
    sponsorName,
    totalPages,
  ]);

  const handleSubmitFilters = useCallback(
    async (data) => {
      try {
        const startDate = data.dateStart
          ? new Date(data.dateStart).toISOString()
          : undefined;
        const endDate = data.dateEnd
          ? new Date(data.dateEnd).toISOString()
          : undefined;
        setSponsorName(data.sponsor);
        setDateStartSelected(startDate);
        setDateEndSelected(endDate);
        await handleLoadSponsors(1, data.sponsor, startDate, endDate);
        setPage(1);
      } catch (error) {
        console.log(error);
      }
    },
    [handleLoadSponsors]
  );

  return (
    <Container className="mt-n3 mb-n3">
      <InfiniteScroll
        id="infinite-scroll"
        onInfiniteLoad={handleLoad}
        className="infinite-scroll py-3"
      >
        <div className="container-fluid py-3">
          <div className="row">
            <div className="zoom col-12 px-0">
              <div className="bg-white d-flex justify-content-between align-items-center p-4">
                <h1 className="mb-0">Patrocinadores</h1>
                <div>
                  <Link
                    to={`${process.env.PUBLIC_URL}/patrocinadores/novo-patrocinador`}
                    className="mb-0 h6 fw-normal text-white btn-new px-4 py-2"
                  >
                    <HiPlus size={16} color="#fff" className="me-2" />
                    Novo Patrocinador
                  </Link>
                </div>
              </div>
            </div>
            <Filter className="col-12 px-0 mt-3">
              <FiltersLists
                onSubmitFilters={handleSubmitFilters}
                sponsor
                date
              />
            </Filter>

            <div className="zoom col-12 pe-0">
              {sponsors.map((sponsor) => (
                <Cards className="bg-white p-3 mt-3">
                  <div className="data-row d-flex  justify-content-between align-items-center">
                    <div>
                      <img src={sponsor.logo} alt="Logo" />
                    </div>
                    <div>
                      <h3 className="mb-1">Nome Patrocinador</h3>
                      <p className="mb-0">{sponsor.sponsor}</p>
                    </div>
                    <div>
                      <h3 className="mb-1">CNPJ</h3>
                      <p className="mb-0">
                        {mask({ value: sponsor.cnpj, kind: 'cnpj' })}
                      </p>
                    </div>
                    <div>
                      <h3 className="mb-1">Data início</h3>
                      <p className="mb-0">
                        {format(sponsor.date, 'dd/MM/yyyy')}
                      </p>
                    </div>
                    <div>
                      <h3 className="mb-1">Total eventos</h3>
                      <p className="mb-0">{sponsor.totalEvents}</p>
                    </div>

                    <div className="text-end">
                      <Link
                        to={`${process.env.PUBLIC_URL}/patrocinadores/editar-patrocinador/${sponsor.id}`}
                        className="border-0 rounded-circle bg-orange me-3"
                      >
                        <KrEdit size={16} color="#fff" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleClickDelete(sponsor)}
                        className="border-0 rounded-circle bg-orange"
                      >
                        <KrClose size={16} color="#fff" />
                      </button>
                    </div>
                  </div>
                </Cards>
              ))}
            </div>
          </div>
        </div>
      </InfiniteScroll>
      <Loading active={loadingMore} text="Carregando..." />
    </Container>
  );
};

export default Sponsors;
